
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { X, CheckCircle, AlertTriangle, Info, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const notificationVariants = cva(
  "group pointer-events-auto relative flex w-full items-start gap-4 overflow-hidden rounded-xl p-6 shadow-lg transition-all duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-4 data-[state=open]:slide-in-from-top-4 border",
  {
    variants: {
      variant: {
        default: "bg-card dark:bg-card border-border/50 text-foreground shadow-xl",
        success: "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200/50 text-green-800 shadow-green-100/50",
        error: "bg-gradient-to-r from-red-50 to-rose-50 border-red-200/50 text-red-800 shadow-red-100/50",
        warning: "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200/50 text-amber-800 shadow-amber-100/50",
        info: "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200/50 text-blue-800 shadow-blue-100/50",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const iconVariants = cva(
  "h-5 w-5 flex-shrink-0 mt-0.5",
  {
    variants: {
      variant: {
        default: "text-primary",
        success: "text-green-600",
        error: "text-red-600",
        warning: "text-amber-600",
        info: "text-blue-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const iconMap = {
  default: null,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

export interface NotificationProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof notificationVariants> {
  onClose?: () => void;
}

const Notification = React.forwardRef<HTMLDivElement, NotificationProps>(
  ({ className, variant = "default", children, onClose, ...props }, ref) => {
    const Icon = iconMap[variant as keyof typeof iconMap];

    return (
      <div
        ref={ref}
        className={cn(notificationVariants({ variant }), className)}
        {...props}
      >
        <div className="flex items-start gap-3 flex-1">
          {Icon && (
            <div className={cn(iconVariants({ variant }))}>
              <Icon className="h-full w-full" />
            </div>
          )}
          <div className="flex-1 min-w-0">{children}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-1.5 text-foreground/50 hover:text-foreground/80 hover:bg-black/5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
      </div>
    );
  }
);
Notification.displayName = "Notification";

const NotificationTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h6
    ref={ref}
    className={cn("text-sm font-semibold leading-none tracking-tight mb-1", className)}
    {...props}
  />
));
NotificationTitle.displayName = "NotificationTitle";

const NotificationDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm font-normal opacity-90 leading-relaxed", className)}
    {...props}
  />
));
NotificationDescription.displayName = "NotificationDescription";

export { Notification, NotificationTitle, NotificationDescription };
